import { ReactNode } from 'react';

// URL Regex - matches http:// and https:// URLs
const URL_REGEX = /(https?:\/\/[^\s]+)/gi;

interface ParsedTextPart {
  type: 'text' | 'url';
  content: string;
}

export function parseTextWithUrls(text: string): ParsedTextPart[] {
  const parts: ParsedTextPart[] = [];
  let lastIndex = 0;
  let match;

  // Reset regex
  URL_REGEX.lastIndex = 0;

  while ((match = URL_REGEX.exec(text)) !== null) {
    // Add text before URL
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex, match.index)
      });
    }

    // Add URL
    parts.push({
      type: 'url',
      content: match[0]
    });

    lastIndex = URL_REGEX.lastIndex;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.substring(lastIndex)
    });
  }

  return parts;
}

export function shortenUrl(url: string, maxLength: number = 40): string {
  if (url.length <= maxLength) {
    return url;
  }

  // Try to show domain + start of path
  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname.replace('www.', '');
    const path = urlObj.pathname + urlObj.search;
    
    if (domain.length >= maxLength - 3) {
      return domain.substring(0, maxLength - 3) + '...';
    }

    const remainingLength = maxLength - domain.length - 3;
    return domain + path.substring(0, remainingLength) + '...';
  } catch {
    // Fallback if URL parsing fails
    return url.substring(0, maxLength - 3) + '...';
  }
}

interface RenderTextWithLinksProps {
  text: string;
  onLinkClick: (url: string) => void;
}

export function renderTextWithLinks({ text, onLinkClick }: RenderTextWithLinksProps): ReactNode {
  const parts = parseTextWithUrls(text);

  return parts.map((part, index) => {
    if (part.type === 'url') {
      const shortUrl = shortenUrl(part.content);
      
      const handleLinkClick = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onLinkClick(part.content);
      };
      
      return (
        <span
          key={index}
          onClick={handleLinkClick}
          onTouchEnd={handleLinkClick}
          style={{
            color: 'var(--primary)',
            textDecoration: 'underline',
            cursor: 'pointer',
            fontWeight: '500',
            wordBreak: 'break-all',
            WebkitTapHighlightColor: 'rgba(43, 167, 102, 0.2)'
          }}
        >
          {shortUrl}
        </span>
      );
    }
    return <span key={index}>{part.content}</span>;
  });
}
